import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';

const variants = {
  default: 'bg-primary-700 text-white hover:bg-primary-600 active:bg-primary-800 shadow-sm',
  outline: 'border border-dark-border bg-transparent text-dark-text hover:bg-dark-hover hover:border-primary-500/50',
  ghost: 'bg-transparent text-dark-text hover:bg-dark-hover',
  destructive: 'bg-error text-white hover:bg-red-600 active:bg-red-700 shadow-sm',
  secondary: 'bg-dark-elevated text-dark-text hover:bg-dark-hover border border-dark-border',
  link: 'text-primary-400 underline-offset-4 hover:underline p-0 h-auto',
  island: 'bg-primary-500 text-white hover:bg-primary-600 active:scale-[0.98] shadow-lg shadow-primary-500/20 hover:shadow-primary-500/30 rounded-full',
} as const;

const sizes = {
  sm: 'min-h-10 px-3 text-sm rounded-md gap-1.5',
  md: 'min-h-11 px-4 text-sm rounded-lg gap-2',
  lg: 'h-12 px-6 text-base rounded-lg gap-2.5',
  icon: 'h-10 w-10 rounded-lg',
} as const;

type ButtonVariant = keyof typeof variants;
type ButtonSize = keyof typeof sizes;

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  asChild?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'default', size = 'md', loading = false, disabled, asChild = false, children, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    // When asChild=true, Slot (Radix react-slot) uses React.Children.only internally.
    // In React 19, `false` is counted as a child, so we must not render the Loader
    // inside a Slot — pass children directly to avoid the multi-child error.
    const content = asChild ? children : (
      <>
        {loading && <Loader2 className="h-4 w-4 animate-spin" />}
        {children}
      </>
    );
    return (
      <Comp
        ref={ref}
        className={cn(
          'inline-flex items-center justify-center font-medium transition-all duration-150',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:ring-offset-dark-bg',
          'disabled:pointer-events-none disabled:opacity-50',
          variants[variant],
          sizes[size],
          className
        )}
        disabled={!asChild ? (disabled || loading) : undefined}
        {...props}
      >
        {content}
      </Comp>
    );
  }
);

Button.displayName = 'Button';
export { Button, type ButtonProps, type ButtonVariant, type ButtonSize };
